import React, { useEffect, useState, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { ApiBaseUrlContext } from '../App';  // Import the context

const checkTokenExpiration = (authToken) => {
  if (!authToken) return true;
  const decodedToken = jwtDecode(authToken);
  const currentTime = Date.now() / 1000;
  return decodedToken.exp < currentTime; // Check if token is expired
};

const ProtectedRoute = ({ authToken, handleLogout, pageName, children, ...rest }) => {
  const apiBaseUrl = useContext(ApiBaseUrlContext);  // Use context to get apiBaseUrl
  const [allowedPages, setAllowedPages] = useState(null);  // Store allowed pages for the role
  const [userid, setUserid] = useState(null);  // Store the userId from the token

  useEffect(() => {
    if (!authToken || checkTokenExpiration(authToken)) {
      handleLogout();  // Log out if the token is expired or invalid
      return;
    }

    const fetchAllowedPages = async () => {
      try {
        const decodedToken = jwtDecode(authToken);  // Decode token to get user role
        const role = decodedToken.user_rights;  // Assuming 'user_rights' contains the role name
        const useridFromToken = decodedToken.userid;
        setUserid(useridFromToken);

        // Admin exception: if userid is 1 (admin), allow access to everything
        if (useridFromToken === 1) {
          setAllowedPages(['all']);  // Special case for admin, can access everything
        } else {
          // Non-admin users fetch allowed pages based on role
          const response = await axios.get(`${apiBaseUrl}/mnt/api/roles/role-page-access/${role}`);
          console.log('Allowed pages for role:', response.data); 
          setAllowedPages(response.data.map(page => page.page_name));  // Extract page names and set them in state
        }

      } catch (error) {
        console.error('Error fetching allowed pages:', error);
      }
    };

    fetchAllowedPages();
  }, [authToken, apiBaseUrl, handleLogout]);

  // If token is invalid or expired, log out and redirect to login
  if (!authToken || checkTokenExpiration(authToken)) {
    // Build the current URL (pathname + search) as the redirect URL
    const currentUrl = window.location.pathname + window.location.search;
    
    handleLogout();
  
    // If the current URL is the fault-error page, include it as a redirect query parameter
    if (currentUrl.startsWith('/fault-error')) {
      return <Navigate to={`/qr-login?redirect=${encodeURIComponent(currentUrl)}`} replace />;
    }
    
  
    // Otherwise, redirect to login without a redirect parameter
    return <Navigate to="/login" replace />;
  }
  
  


  // If allowedPages is still null and not an admin, show loading state
  if (allowedPages === null && userid !== 1) {
    return <div>Loading...</div>;
  }

  // If not admin and the page is not allowed, redirect to unauthorized
  if (allowedPages !== 'all' && !allowedPages.includes(pageName) && userid !== 1) {
    return <Navigate to="/unauthorized" replace />;
  }

  // If all checks pass, render the child component with all props
  return React.cloneElement(children, { ...rest });
};

export default ProtectedRoute;
